import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { UsersIcon, AwardIcon, HeartIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  return <section id="about" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-5">
      <div className="relative z-10 mx-auto max-w-7xl" data-id="element-6">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12" data-id="element-7">
          {/* Left side - Team Animation */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="order-2 lg:order-1" data-id="element-8">
            <div className="rounded-2xl border p-8 sm:p-12 relative overflow-hidden min-h-[400px] flex items-center justify-center" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-9">
              <AbstractTeamAnimation data-id="element-10" />
            </div>
          </motion.div>

          {/* Right side - Content */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="order-1 lg:order-2" data-id="element-11">
            <div className="rounded-2xl border p-6 sm:p-8" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-12">
              <h2 className="mb-6 text-2xl sm:text-3xl font-bold" style={{
              color: 'var(--text-primary)'
            }} data-id="element-13">
                {t('about.title')}{' '}
                <span style={{
                color: 'var(--accent)'
              }} data-id="element-14">
                  {t('about.titleHighlight')}
                </span>
              </h2>

              <p className="mb-4 text-sm sm:text-base leading-relaxed" style={{
              color: 'var(--text-primary)'
            }} data-id="element-15">
                {t('about.p1')}
              </p>

              <p className="mb-8 text-sm sm:text-base leading-relaxed" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-16">
                {t('about.p2')}
              </p>

              {/* 3 Icon Cards */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-17">
                {[{
                icon: UsersIcon,
                label: t('about.icon1')
              }, {
                icon: AwardIcon,
                label: t('about.icon2')
              }, {
                icon: HeartIcon,
                label: t('about.icon3')
              }].map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} animate={isInView ? {
                opacity: 1,
                y: 0
              } : {}} transition={{
                duration: 0.5,
                delay: 0.5 + index * 0.1
              }} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)'
              }} data-id="element-18">
                    <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg flex-shrink-0" style={{
                  backgroundColor: 'var(--bg-secondary)'
                }} data-id="element-19">
                      <item.icon className="h-4 w-4 sm:h-5 sm:w-5" style={{
                    color: 'var(--accent)'
                  }} data-id="element-20" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium leading-tight" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-21">
                      {item.label}
                    </span>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function AbstractTeamAnimation() {
  // Create abstract team members as colorful circles
  const teamMembers = [{
    color: '#3b82f6',
    size: 60,
    x: 0,
    y: -80
  }, {
    color: '#8b5cf6',
    size: 50,
    x: -70,
    y: 0
  }, {
    color: '#ec4899',
    size: 55,
    x: 70,
    y: 0
  }, {
    color: '#10b981',
    size: 45,
    x: -50,
    y: 70
  }, {
    color: '#f59e0b',
    size: 50,
    x: 50,
    y: 70
  }];
  return <div className="relative w-full h-full flex items-center justify-center" data-id="element-22">
      {/* Central hub */}
      <motion.div animate={{
      scale: [1, 1.05, 1]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute w-20 h-20 rounded-full flex items-center justify-center z-10" style={{
      backgroundColor: 'var(--bg-tertiary)',
      border: '2px solid var(--border)'
    }} data-id="element-23">
        <UsersIcon className="w-10 h-10" style={{
        color: 'var(--accent)'
      }} data-id="element-24" />
      </motion.div>

      {/* Team members orbiting */}
      {teamMembers.map((member, i) => <motion.div key={i} initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1,
      y: [0, -10, 0]
    }} transition={{
      opacity: {
        duration: 0.5,
        delay: i * 0.1
      },
      scale: {
        duration: 0.5,
        delay: i * 0.1
      },
      y: {
        duration: 2 + i * 0.3,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.2
      }
    }} className="absolute rounded-full shadow-lg" style={{
      width: member.size,
      height: member.size,
      backgroundColor: member.color,
      transform: `translate(${member.x}px, ${member.y}px)`,
      opacity: 0.8
    }} data-id="element-25">
          {/* Inner glow */}
          <div className="absolute inset-2 rounded-full" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.3)'
      }} data-id="element-26" />
        </motion.div>)}

      {/* Connecting lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-27">
        {teamMembers.map((member, i) => <motion.line key={i} x1="50%" y1="50%" x2={`calc(50% + ${member.x}px)`} y2={`calc(50% + ${member.y}px)`} stroke="var(--border)" strokeWidth="2" strokeDasharray="4 4" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 0.3
      }} transition={{
        duration: 1,
        delay: 0.5 + i * 0.1
      }} data-id="element-28" />)}
      </svg>

      {/* Floating particles */}
      {[...Array(8)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full" style={{
      backgroundColor: 'var(--accent)',
      opacity: 0.4
    }} animate={{
      x: [Math.random() * 200 - 100, Math.random() * 200 - 100, Math.random() * 200 - 100],
      y: [Math.random() * 200 - 100, Math.random() * 200 - 100, Math.random() * 200 - 100],
      opacity: [0, 0.6, 0],
      scale: [0, 1, 0]
    }} transition={{
      duration: 4 + Math.random() * 2,
      repeat: Infinity,
      delay: Math.random() * 2
    }} data-id="element-29" />)}

      {/* Label */}
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="absolute bottom-4 text-sm font-medium" style={{
      color: 'var(--text-muted)'
    }} data-id="element-30">
        Our Creative Team
      </motion.p>
    </div>;
}